import { create } from 'zustand'
import { io, Socket } from 'socket.io-client'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { gql } from 'graphql-request'

export interface Message {
  id: string
  content: string
  role: 'user' | 'ai' | 'system' | 'assistant'
  timestamp: Date
  payload?: Record<string, any>
}

export interface FlowMessage {
  role: 'user' | 'assistant' | 'system' | 'tool'
  content?: string
}

export interface FlowResponse {
  message: FlowMessage
  state?: Record<string, any>
  prompts?: { foldName: string; content: string }[]
}

interface ChatState {
  socket: Socket | null
  messages: Message[]
  isConnected: boolean
  isLoading: boolean
  isReady: boolean
  reconnectAttempts: number
  maxReconnectAttempts: number
  reconnectTimeout: NodeJS.Timeout | null
  heartbeatInterval: NodeJS.Timer | null
  savedQuery: Record<string, string | number> | null
  savedCallback: (() => void) | null
  sessionToken: string | null

  initSocket: (query: Record<string, string | number>, callback: () => void) => Promise<void>
  disconnectSocket: () => void
  sendMessage: (content: string, role?: 'user' | 'system' | 'assistant', state?: Record<number, any>, extraMessages?: Message[], opts?: { skipLocalEcho?: boolean }) => Promise<void>
  clearMessages: () => void
}

type ISet = {
  (partial: ChatState | Partial<ChatState> | ((state: ChatState) => ChatState | Partial<ChatState>), replace?: false): void
  (state: ChatState | ((state: ChatState) => ChatState), replace: true): void
}

type IGet = () => ChatState

const chatStoreFn = (set: ISet, get: IGet) => ({
  socket: null,
  messages: [],
  isConnected: false,
  isLoading: false,
  isReady: false,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  reconnectTimeout: null,
  heartbeatInterval: null,
  savedQuery: null,
  savedCallback: null,
  sessionToken: null,

  initSocket: async (query: Record<string, string | number>, callback: () => void) => {
    try {
      set({ isLoading: true, savedQuery: query, savedCallback: callback })

      const state = get()
      let token = state.sessionToken

      // Only get new token if we don't have one stored
      if (!token) {
        // Get session token from Georgia API
        const GET_MICROFRANK_TOKEN = gql`
          query GetMicrofrankToken {
            getMicrofrankToken
          }
        `

        const { getMicrofrankToken } = await authorizedGraphQLRequest<{
          getMicrofrankToken: string
        }>(GET_MICROFRANK_TOKEN)

        if (!getMicrofrankToken) {
          throw new Error('Failed to get Microfrank session token')
        }

        token = getMicrofrankToken
        set({ sessionToken: token })
      }

      const socket = io(process.env.VITE_WS_URL, {
        // auth: { token },
        // path: '/ws/socket.io',
        query: {
          ...query,
          sessionToken: token,
        },
        transports: ['websocket'], // Force WebSocket only, no polling
        upgrade: false, // Don't start with polling
        reconnection: true, // Enable automatic reconnection
        reconnectionAttempts: 5, // Try 5 times
        reconnectionDelay: 1000, // Start with 1 second delay
        reconnectionDelayMax: 5000, // Maximum 5 seconds between attempts
        timeout: 5000, // Connection timeout
      })

      // Store socket immediately but mark as not connected
      set({ socket, isConnected: false, isLoading: true, isReady: false, reconnectAttempts: 0 })

      socket.on('connect', () => {
        set({ isConnected: true, isLoading: false })
      })

      // Wait for backend 'ready' before resolving so voice/video only start sending after session is ready
      await new Promise<void>((resolve, reject) => {
        socket.once('ready', (data: { status: string; flowId: number }) => {
          set({ isReady: true, isLoading: false })
          callback()
          resolve()
        })
        socket.once('connect_error', (err: Error) => reject(err))
      })

      socket.on('ready', (data: { status: string; flowId: number }) => {
        set({ isReady: true, isLoading: false })
        callback()
      })

      socket.on('heartbeat-init', (data: { interval: number }) => {
        // console.log('Starting heartbeat with interval:', data.interval)
        const state = get()

        // Clear existing interval if any
        if (state.heartbeatInterval) {
          clearInterval(state.heartbeatInterval)
        }

        // Start sending heartbeats
        const interval = setInterval(() => {
          const { socket } = get()
          if (socket && socket.connected) {
            socket.emit('heartbeat')
          }
        }, data.interval)

        set({ heartbeatInterval: interval })
      })

      socket.on('connect_error', (error) => {
        set({ isLoading: false, isConnected: false })
      })

      socket.on('disconnect', () => {
        set({ isConnected: false, isReady: false })
      })

      // In Socket.IO v4, reconnect lifecycle events live on the Manager (`socket.io`), not the socket itself.
      socket.io.on('reconnect_attempt', (attemptNumber) => {
        set({ reconnectAttempts: attemptNumber })
      })

      socket.io.on('reconnect', () => {
        set({ isConnected: true, reconnectAttempts: 0, isReady: true, isLoading: false })
      })

      socket.io.on('reconnect_error', (error) => {
        console.error('Reconnection error:', error)
      })

      socket.io.on('reconnect_failed', () => {
        console.error('Failed to reconnect after maximum attempts')
        set({ isConnected: false, isLoading: false })
      })

      socket.on('response', (data: FlowResponse) => {
        // Handle different response formats
        // console.log('Received response:', data)
        if (!data?.message) {
          set({
            // messages: [...state.messages],
            isLoading: false,
          })
          return
        }

        // Only add assistant messages; user messages are added optimistically in sendMessage (avoid echo duplicate)
        if (data.message.role !== 'assistant') {
          set({ isLoading: false })
          return
        }

        const newMessage: Message = {
          id: Date.now().toString(),
          content: data.message.content || '',
          role: 'ai',
          timestamp: new Date(),
          payload: data.state || {},
        }

        // console.info(data.prompts?.map(p => `${p.foldName}: ${p.content}`).join('\n---\n'))

        set((state) => ({
          messages: [...state.messages, newMessage],
          isLoading: false,
        }))
      })

      socket.on('error', (error) => {
        set({ isLoading: false })
      })
    } catch (error) {
      set({ isLoading: false })
    }
  },

  disconnectSocket: () => {
    const { socket, reconnectTimeout, heartbeatInterval, messages, sessionToken } = get()

    // Fire-and-forget: save displayed messages to Georgia server
    if (sessionToken && messages.length > 0) {
      const displayableMessages = messages
        .filter((msg) => (msg.role === 'user' || msg.role === 'ai' || msg.role === 'assistant') && msg.content?.trim())
        .map((msg) => ({
          role: msg.role === 'ai' ? 'assistant' : msg.role,
          content: msg.content,
          timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : String(msg.timestamp),
        }))

      if (displayableMessages.length > 0) {
        const SAVE_CHAT_HISTORY = gql`
          mutation SaveChatHistory($input: SaveChatHistoryInput!) {
            saveChatHistory(input: $input) {
              success
              message
            }
          }
        `
        authorizedGraphQLRequest(SAVE_CHAT_HISTORY, {
          input: { sessionToken, messages: displayableMessages },
        }).catch((err) => console.error('[disconnectSocket] save failed:', err))
      }
    }

    if (socket) {
      socket.disconnect()
    }
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout)
    }
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval)
    }
    set({
      socket: null,
      isConnected: false,
      isReady: false,
      messages: [],
      isLoading: false,
      reconnectAttempts: 0,
      reconnectTimeout: null,
      heartbeatInterval: null,
      savedQuery: null,
      savedCallback: null,
      sessionToken: null, // Clear session token on manual disconnect
    })
  },

  sendMessage: async (content: string, role: 'user' | 'system' | 'assistant' = 'user', state?: Record<number, any>, extraMessages?: Message[], opts?: { skipLocalEcho?: boolean }) => {
    const storeState = get()
    const { socket, messages } = storeState

    // skipLocalEcho: the message was already appended optimistically (e.g. lazy-connect first reply),
    // so only emit it — don't add a duplicate bubble.
    if (opts?.skipLocalEcho) {
      set({ isLoading: true })
    } else {
      const newMessage: Message = {
        id: Date.now().toString(),
        content,
        role,
        timestamp: new Date(),
      }

      set({
        messages: [...messages, newMessage],
        isLoading: true,
      })
    }

    if (socket && socket.connected) {
      const messagePayload = {
        messages: [
          ...(extraMessages || []),
          {
            role,
            content,
          },
        ],
        state,
      }

      socket.emit('message', messagePayload)
    }
  },

  clearMessages: () => {
    set({ messages: [] })
  },
})

export const useChatStore = create<ChatState>(chatStoreFn)
export const useCollectorChatStore = create<ChatState>(chatStoreFn)
export const useRoleChatStore = create<ChatState>(chatStoreFn)
export const useFeedbackChatStore = create<ChatState>(chatStoreFn)
export const useReviewChatStore = create<ChatState>(chatStoreFn)
